import {useAppDispatch} from '@/store'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {useCallback, useMemo} from 'react'
import {MESSAGES, UI_CONFIG} from './InventoryListConstants'
import {getSelectedRowData} from '@/utils/commons'

/**
 * Custom hook for inventory list business logic
 * @param {Object} inventoryData - The inventory data
 * @param {Array} rowSelected - Selected rows
 * @param {Function} onDownloadBtnClick - Download callback
 * @param {Function} setSortingObj - Sorting setter
 * @param {Function} setSearchArry - Search setter
 * @param {Function} setRowSelected - Row selection setter
 * @param {Function} createSearchArray - Search array creator
 */
export const useInventoryActions = ({
  inventoryData,
  rowSelected,
  onDownloadBtnClick,
  setSortingObj,
  setSearchArry,
  setRowSelected,
  createSearchArray,
}) => {
  const dispatch = useAppDispatch()

  // Derive the selected rows data from the indices supplied by the data-table
  // This is memoised so the calculation runs only when dependencies change
  const selectedDataList = useMemo(() => {
    // getSelectedRowData is mocked in tests, so returning whatever it provides
    return getSelectedRowData(rowSelected, inventoryData?.data) || []
  }, [rowSelected, inventoryData])

  // Memoize the extracted product IDs array (value, not function)
  const extractIds = useMemo(() => selectedDataList?.map(item => item?.productId) || [], [selectedDataList])

  // Memoize the inventory IDs based on selection limit (value, not function)
  const inventoryIds = useMemo(() => {
    return extractIds.length === UI_CONFIG.MAX_SELECTION_COUNT ? null : extractIds
  }, [extractIds])

  // Memoize the download click handler (function)
  const handleDownloadClick = useCallback(async () => {
    // Add defensive check for selectedDataList
    if (!selectedDataList || selectedDataList.length === 0) {
      dispatch(
        setToastNotifications({
          show: true,
          message: MESSAGES.NO_SELECTION_ERROR,
          type: 'error',
        })
      )
      return
    }

    // Additional check for productId existence
    const validItems = selectedDataList.filter(item => item?.productId)
    if (validItems.length === 0) {
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Selected items do not have valid product IDs',
          type: 'error',
        })
      )
      return
    }

    try {
      await onDownloadBtnClick(validItems)
    } catch (error) {
      console.error('Download error:', error)
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Failed to download CSV file. Please try again.',
          type: 'error',
        })
      )
    }
  }, [selectedDataList, onDownloadBtnClick, dispatch])

  const handleResetAll = useCallback(() => {
    setSortingObj({})
    createSearchArray([])
    setSearchArry({})
    setRowSelected([])
  }, [setSortingObj, createSearchArray, setSearchArry, setRowSelected])

  return {
    selectedDataList,
    onDownloadClick: handleDownloadClick,
    handleResetAll,
    extractIds,
    inventoryIds,
  }
}
